import React, { useRef } from "react";
import PropTypes from "prop-types";
import { Box, TextField, Grid, Card, CardContent, Typography, IconButton } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import LinkIcon from "@mui/icons-material/Link";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import Select from "react-select";

function DataSourceStep({ dataSources, setDataSources }) {
  const contentTypes = [
    { value: "contacts", label: "Contacts" },
    { value: "waste", label: "Waste Collection" },
    { value: "news", label: "News" },
    { value: "xml", label: "Products (XML Feed)" },
    { value: "pdf", label: "PDF" },
    { value: "doc", label: "DOC" },
    { value: "text", label: "Text file" },
  ];

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "45px",
      borderColor: state.isFocused ? "#5a49da" : "rgba(0, 0, 0, 0.23)",
      boxShadow: state.isFocused ? "0 0 0 1px #5a49da" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#5a49da" : "rgba(0, 0, 0, 0.87)",
      },
      marginBottom: "24px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "rgba(0, 0, 0, 0.6)",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#5a49da"
        : state.isFocused
        ? "rgba(90, 73, 218, 0.1)"
        : "white",
      color: state.isSelected ? "white" : "black",
      "&:hover": {
        backgroundColor: state.isSelected ? "#5a49da" : "rgba(90, 73, 218, 0.1)",
      },
    }),
  };
  const handleTypeChange = (selectedOption) => {
    setDataSources([{ ...dataSources[0], knowledgeType: selectedOption.value }]);
  };

  const handleUrlChange = (e) => {
    setDataSources([{ ...dataSources[0], url: e.target.value }]);
  };

  const handleFileSelect = (e) => {
    // File upload logic to be implemented later
    console.log(e.target.files[0]);
  };

  const inputRef = useRef(null);

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              borderLeft: "4px solid",
              borderColor: "#5a49da",
              height: "100%",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <PsychologyOutlinedIcon sx={{ mr: 1, color: "#5a49da" }} />
                <MDTypography variant="h6" fontWeight="bold">
                  Add Knowledge Source
                </MDTypography>
              </Box>

              <MDTypography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
                Source URL or File
              </MDTypography>
              <TextField
                fullWidth
                placeholder="https://example.com/feed.xml"
                value={dataSources[0]?.url || ""}
                onChange={handleUrlChange}
                variant="outlined"
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <IconButton>
                      <LinkIcon />
                    </IconButton>
                  ),
                }}
              />

              <MDTypography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
                Content Type
              </MDTypography>
              <Select
                styles={customStyles}
                options={contentTypes}
                onChange={handleTypeChange}
                value={contentTypes.find((o) => o.value === dataSources[0]?.knowledgeType) || null}
                placeholder="Select content type..."
              />

              <input
                type="file"
                ref={inputRef}
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xml,.txt"
                hidden
              />
              <Box
                sx={{
                  border: "2px dashed #d0c2ff",
                  borderRadius: "12px",
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "#8A2BE2",
                  },
                  mb: 3,
                }}
                onClick={() => inputRef.current.click()}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 40, color: "primary.main" }} />
                <MDTypography variant="h6" fontWeight="regular">
                  Drag & drop files here or{" "}
                  <Typography component="span" variant="h6" color="primary">
                    browse
                  </Typography>
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  Supports PDF, DOC, XML, and text files
                </MDTypography>
              </Box>

              <MDButton variant="gradient" color="info" fullWidth sx={{ py: 1.5 }}>
                Add Knowledge Source
              </MDButton>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 3,
              borderRight: "4px solid",
              borderColor: "success.main",
            }}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <PsychologyOutlinedIcon sx={{ fontSize: 60, color: "#5a49da", mb: 2 }} />
              <MDTypography variant="h5" fontWeight="bold" mb={1}>
                Knowledge Preview
              </MDTypography>
              <MDTypography variant="body2" color="text" mb={1}>
                Your chatbot will use these sources to provide accurate answers
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Add knowledge sources to get started
              </MDTypography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

DataSourceStep.propTypes = {
  dataSources: PropTypes.array.isRequired,
  setDataSources: PropTypes.func.isRequired,
};

export default DataSourceStep;
